import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import type { TimeLog } from "@/types/db";
import { fmtTime, fmtHours, todayISO } from "@/lib/utils";
import { WebcamConfirmModal } from "@/components/WebcamConfirmModal";
import { useIsMobile } from "@/hooks/useIsMobile";
import { CelebrationsBanner } from "@/components/CelebrationsBanner";
import { DailyRitualCard } from "@/components/DailyRitualCard";
import { NewHireChecklist } from "@/components/NewHireChecklist";
import { AdminAttention } from "@/components/AdminAttention";
import { NeedsAttention } from "@/components/NeedsAttention";
import { isAdmin } from "@/lib/utils";

export function Dashboard() {
  const { employee, refreshEmployee } = useAuth();
  const [logs, setLogs] = useState<TimeLog[]>([]);
  const [openLog, setOpenLog] = useState<TimeLog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tick, setTick] = useState(0); // for live elapsed counter
  const [modalOpen, setModalOpen] = useState<"in" | "out" | null>(null);
  const [sickOpen, setSickOpen] = useState(false);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  async function refresh() {
    if (!employee) return;
    const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();
    const { data, error } = await supabase
      .from("hq_time_logs")
      .select("*")
      .eq("employee_id", employee.id)
      .gte("clock_in_time", since)
      .order("clock_in_time", { ascending: false });
    if (error) setError(error.message);
    else {
      const all = data as TimeLog[];
      setLogs(all);
      setOpenLog(all.find(l => !l.clock_out_time) ?? null);
    }
  }

  function flashSuccess(msg: string) {
    setSuccess(msg);
    window.setTimeout(() => setSuccess(null), 4000);
  }

  async function clockIn(photoPath: string | null) {
    setError(null);
    setSuccess(null);
    const { error } = await supabase.rpc("hq_clock_in", { p_photo_path: photoPath });
    if (error) { setError("Clock in failed: " + error.message); throw error; }
    setModalOpen(null);
    await refresh();
    await refreshEmployee();
    flashSuccess("✓ Clocked in. Have a great shift!");
  }

  async function clockOut(photoPath: string | null) {
    setError(null);
    setSuccess(null);
    const { error } = await supabase.rpc("hq_clock_out", { p_notes: null, p_photo_path: photoPath });
    if (error) { setError("Clock out failed: " + error.message); throw error; }
    setModalOpen(null);
    await refresh();
    await refreshEmployee();
    flashSuccess("✓ Clocked out. See you next time!");
  }

  // tick acts as a re-render trigger so the live counter updates each second
  void tick;

  const todayHrs = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    let h = 0;
    for (const l of logs) {
      const start = new Date(l.clock_in_time);
      if (start >= today) {
        const end = l.clock_out_time ? new Date(l.clock_out_time) : new Date();
        h += (end.getTime() - start.getTime()) / 3_600_000;
      }
    }
    return h;
  }, [logs, tick]);

  const weekHrs = useMemo(() => {
    const monday = new Date();
    const day = monday.getDay();
    const diff = monday.getDate() - day + (day === 0 ? -6 : 1);
    monday.setDate(diff); monday.setHours(0,0,0,0);
    let h = 0;
    for (const l of logs) {
      const start = new Date(l.clock_in_time);
      if (start >= monday) {
        const end = l.clock_out_time ? new Date(l.clock_out_time) : new Date();
        h += (end.getTime() - start.getTime()) / 3_600_000;
      }
    }
    return h;
  }, [logs, tick]);

  const liveElapsed = useMemo(() => {
    if (!openLog) return "0:00:00";
    const ms = Date.now() - new Date(openLog.clock_in_time).getTime();
    const s = Math.floor(ms / 1000) % 60;
    const m = Math.floor(ms / 60000) % 60;
    const h = Math.floor(ms / 3_600_000);
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [openLog, tick]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          Hi, {employee?.first_name || "there"} 👋
        </h1>
        <p className="text-sm text-gray2">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
      </div>

      {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
      {success && <div className="rounded-lg bg-ok-bg px-3.5 py-2.5 text-sm font-medium text-ok">{success}</div>}

      <NewHireChecklist />
      <DailyRitualCard />


      <CelebrationsBanner />

      {/* Clock In/Out hero — the dominant action */}
      <div className="card shadow-soft">
        <div className="flex flex-col items-center gap-5 md:flex-row md:justify-between">
          <div className="text-center md:text-left">
            <div className="text-xs uppercase tracking-wider text-gray2">Status</div>
            <div className="mt-1 text-3xl font-bold md:text-4xl">
              {openLog ? <span className="text-ok">Clocked In</span> : <span className="text-gray2">Off the clock</span>}
            </div>
            {openLog && (
              <div className="mt-2 text-sm text-gray2">
                Since {fmtTime(openLog.clock_in_time)} · <span className="font-mono text-ink">{liveElapsed}</span>
              </div>
            )}
            <div className="mt-3 flex justify-center gap-5 text-xs md:justify-start">
              <div>
                <div className="uppercase tracking-wider text-gray2">Today</div>
                <div className="text-lg font-semibold text-teal-dark">{fmtHours(todayHrs)}</div>
              </div>
              <div>
                <div className="uppercase tracking-wider text-gray2">This week</div>
                <div className="text-lg font-semibold text-teal-dark">{fmtHours(weekHrs)}</div>
              </div>
            </div>
          </div>
          <ClockActionButton openLog={openLog} onClick={k => setModalOpen(k)} />
        </div>
      </div>

      {employee && (
        <WebcamConfirmModal
          open={modalOpen !== null}
          title={modalOpen === "out" ? "Clock out?" : "Clock in?"}
          description={modalOpen === "out"
            ? "We'll save a photo with the clock-out for verification."
            : "We'll save a photo with the clock-in for verification."}
          confirmLabel={modalOpen === "out" ? "Clock Out" : "Clock In"}
          employeeId={employee.id}
          onClose={() => setModalOpen(null)}
          onConfirmed={async path => {
            if (modalOpen === "in") await clockIn(path);
            else if (modalOpen === "out") await clockOut(path);
          }}
        />
      )}

      {/* Unified attention card — training due, denied PTO, urgent notifs, sick day shortcut */}
      <NeedsAttention onCallOutSick={() => setSickOpen(true)} />

      {/* Admin-only team pulse */}
      {isAdmin(employee?.role) && <AdminAttention />}

      <SickDayModal open={sickOpen} onClose={() => setSickOpen(false)} />
    </div>
  );
}

function ClockActionButton({ openLog, onClick }: { openLog: TimeLog | null; onClick: (k: "in" | "out") => void }) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <div className="w-full rounded-lg bg-warn-bg p-4 text-center text-sm text-warn md:w-auto md:max-w-xs">
        <div className="font-semibold">Clock in from a desktop</div>
        <div className="mt-1 text-xs">For accurate time tracking, please clock in on a desktop or the warehouse kiosk.</div>
      </div>
    );
  }
  if (openLog) {
    return (
      <button onClick={() => onClick("out")} className="btn btn-lg w-full bg-bad hover:bg-bad/90 md:w-auto">Clock Out</button>
    );
  }
  return (
    <button onClick={() => onClick("in")} className="btn btn-lg w-full md:w-auto">Clock In</button>
  );
}

function SickDayModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { employee } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");

  if (!open || !employee) return null;

  async function submit() {
    if (!employee) return;
    setBusy(true); setError(null);
    const today = todayISO();
    const { data: req, error: insErr } = await supabase.from("hq_pto_requests").insert({
      employee_id: employee.id,
      start_date: today,
      end_date: today,
      reason: "sick",
      notes: note.trim() || "Called out sick from dashboard",
    }).select("id").single();
    if (insErr) { setError(insErr.message); setBusy(false); return; }

    const { data: admins } = await supabase.from("hq_employees")
      .select("id").in("role", ["admin", "super_admin"]).eq("status", "active");
    if (admins && admins.length) {
      await supabase.from("hq_notifications").insert(admins.map(a => ({
        recipient_id: (a as { id: string }).id,
        preview: `🤒 ${employee.first_name ?? employee.email} called out sick today.`,
        category: "callout_sick",
        link: "/admin/pto",
        source_id: (req as { id: string }).id,
      })));
    }
    setBusy(false); setDone(true);
    setTimeout(() => { onClose(); setDone(false); setNote(""); }, 2000);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={() => !busy && onClose()}>
      <div className="card w-full max-w-md" onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="text-center text-sm text-ok">Thanks — admin has been notified. Feel better.</div>
        ) : (
          <>
            <h2 className="mb-2 text-lg font-semibold">Call out sick today?</h2>
            <p className="mb-3 text-xs text-gray2">This submits a same-day time off request marked "sick" and pings admin.</p>
            {error && <div className="mb-3 rounded bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}
            <label className="label">Anything you want admin to know? (optional)</label>
            <textarea className="input min-h-[80px]" value={note} onChange={e => setNote(e.target.value)} placeholder="Fever, can't come in, etc." />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={onClose} disabled={busy} className="btn-secondary">Cancel</button>
              <button onClick={() => void submit()} disabled={busy} className="btn">{busy ? "Sending…" : "Call out sick"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
